import { useEffect, useRef, useState } from "react";
import { resolveServer } from "../lib/server-config";

// Site-wide outage banner. Mounted ABOVE Shell in main.tsx so it shows on
// EVERY surface from one place — marketing landing, access-code gate, the
// wizard, the run screen, the deep-link landing, the operator console. An
// outage matters to a visitor whether or not they've started a run.
//
// Two states, one probe:
//   degraded    — /healthz answers and reports `anthropic_service_degraded`
//                 (the operator's manual flag OR the cached background probe;
//                 poller-refreshed, no live probe on the request path).
//   unreachable — /healthz doesn't answer at all: the host is down (2026-10-17,
//                 provider power-cycled the VPS for 14 minutes), the app
//                 container is gone (502/504 from traefik), or Cloudflare
//                 can't reach the origin (52x). Confirmed by a second probe
//                 a few seconds later so one dropped request can't raise it,
//                 then re-probed on a short cadence so the bar clears itself
//                 promptly once the server is back.
//
// The probe goes to the API origin the wizard itself uses (resolveServer),
// NOT same-origin "/healthz": on cloud.plaintosee.cc that path is answered by
// Cloudflare Pages with the SPA's index.html — a 200 that is not JSON — so a
// same-origin probe could never see the API, up or down. (That was the state
// of this banner until 2026-10-17; it was inert in prod.) In dev,
// resolveServer is same-origin and the Vite proxy forwards /healthz.
//
// Inline styles, deliberately (mirrors StaleBuildBanner): the bar sits atop
// surfaces with different palettes and must not depend on their CSS being
// loaded or win specificity fights. Explicit serif stack per the
// cross-platform rule (no system-ui) — matches the wizard's --serif value.

const RECHECK_MIN_MS = 30_000;
const HEARTBEAT_MS = 90_000; // backend cache refreshes ~every 90s
const CONFIRM_MS = 4_000; // second look before calling the server unreachable
const RETRY_MS = 20_000; // while unreachable: look again sooner than the heartbeat

export type OutageState = "ok" | "degraded" | "unreachable";
// null = couldn't tell (a 4xx, a non-JSON body): leave the bar as-is, don't flap.
export type OutageProbe = () => Promise<OutageState | null>;

const BAR: React.CSSProperties = {
  boxSizing: "border-box",
  width: "100%",
  padding: "11px 22px",
  textAlign: "center",
  background: "#fbe6c4",
  color: "#7a4a05",
  fontFamily:
    "'Iowan Old Style', 'Iowan', 'Charter', Georgia, 'Times New Roman', serif",
  fontSize: 14,
  lineHeight: 1.45,
  boxShadow: "inset 0 -1px 0 rgba(122,74,5,0.28)",
};

// Unreachable reads in the error register (the wizard's --err family), not
// the degraded amber: "slower" and "down" must not look like the same news.
const BAR_DOWN: React.CSSProperties = {
  ...BAR,
  background: "#fbeaea",
  color: "#7a1f1f",
  boxShadow: "inset 0 -1px 0 rgba(160,26,26,0.28)",
};

async function probeServer(): Promise<OutageState | null> {
  let r: Response;
  try {
    r = await fetch(`${resolveServer().replace(/\/$/, "")}/healthz`);
  } catch {
    // Network-level failure. In prod a dead origin lands HERE, not in the
    // status branch: Cloudflare's 52x page has no Access-Control headers, so
    // the cross-origin fetch is blocked and rejects.
    return "unreachable";
  }
  // /healthz itself only ever answers 200; any 5xx is a gateway or Cloudflare
  // speaking for an app that didn't.
  if (r.status >= 500) return "unreachable";
  if (!r.ok) return null;
  try {
    const j = await r.json();
    return j.anthropic_service_degraded === true ? "degraded" : "ok";
  } catch {
    return null; // not our JSON (an HTML page) → unknown, don't flap
  }
}

export function OutageBanner({
  // Overridable for tests; defaults to the live /healthz probe on the API origin.
  probe = probeServer,
  confirmMs = CONFIRM_MS,
  retryMs = RETRY_MS,
}: {
  probe?: OutageProbe;
  confirmMs?: number;
  retryMs?: number;
}) {
  const [state, setState] = useState<OutageState>("ok");
  const lastCheck = useRef(0);
  const failures = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    function lookAgainIn(ms: number) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void run(), ms);
    }

    async function run() {
      lastCheck.current = Date.now();
      const s = await probe();
      if (cancelled || s === null) return;
      if (s === "unreachable") {
        failures.current += 1;
        // One miss is a blip (a dropped request, a tab waking from sleep);
        // ask once more before alarming anyone.
        if (failures.current < 2) {
          lookAgainIn(confirmMs);
          return;
        }
        setState("unreachable");
        lookAgainIn(retryMs);
        return;
      }
      failures.current = 0;
      setState(s);
    }

    // Initial check runs unconditionally so it survives StrictMode's
    // dev double-invoke (setup → cleanup → setup); the throttle only guards
    // the repeat triggers (visibility + heartbeat) so they can't hammer.
    void run();

    function throttledRun() {
      const now = Date.now();
      if (now - lastCheck.current < RECHECK_MIN_MS) return;
      void run();
    }
    function onVisible() {
      if (document.visibilityState === "visible") throttledRun();
    }
    document.addEventListener("visibilitychange", onVisible);
    const beat = window.setInterval(() => {
      if (document.visibilityState === "visible") throttledRun();
    }, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(beat);
    };
  }, [probe, confirmMs, retryMs]);

  if (state === "ok") return null;
  if (state === "unreachable") {
    return (
      <div style={BAR_DOWN} role="alert">
        <strong style={{ fontWeight: 600 }}>
          Aurora&rsquo;s server isn&rsquo;t reachable right now.
        </strong>{" "}
        This usually clears within a few minutes, and this bar goes away on
        its own. Your file stays on your computer. If you&rsquo;ve already
        paid, your access code arrives by email, and entering it again never
        charges you twice.
      </div>
    );
  }
  return (
    <div style={BAR} role="status">
      <strong style={{ fontWeight: 600 }}>
        Heads up — slower than usual today.
      </strong>{" "}
      Anthropic (the AI service Aurora runs on) is reporting degraded
      performance, so analyses may take longer to finish. Your run will still
      complete and we&rsquo;ll email you the moment it&rsquo;s ready.
    </div>
  );
}
